import { useQuery } from '@tanstack/react-query';

import { useMemo } from 'react';

import type { ConversationChannel, ConversationStatus } from '@ekhodealer/ekho-common';
import type { SearchConversationsParams } from './useSearchConversations';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { queries } from '@config/server/queryKeys';

import { searchConversations } from './useSearchConversations';

export interface ConversationFacetCounts {
	/** Counts of conversations grouped by channel type. */
	channelCounts: Partial<Record<ConversationChannel, number>>;
	/** Counts of conversations grouped by status. */
	statusCounts: Partial<Record<ConversationStatus, number>>;
}

/**
 * Hook to get facet counts for conversation filter options.
 * Makes a separate search request with limit:0 and facets:['channels','status']
 * so counts remain stable regardless of which channel/status filters are applied.
 */
export function useConversationFacets(
	searchParams?: SearchConversationsParams,
): ConversationFacetCounts {
	const servicesProviderId = useCurrentServicesProviderId();

	const baseParams = useMemo<SearchConversationsParams>(
		() => ({
			...searchParams,
			limit: 0,
			facets: ['channels', 'status'],
			filters: {
				leadId: searchParams?.filters?.leadId,
			},
		}),
		[searchParams],
	);

	const { data } = useQuery({
		queryKey: queries.conversations.facets(servicesProviderId, baseParams).queryKey,
		queryFn: () => searchConversations(servicesProviderId, baseParams, 0),
		enabled: Boolean(servicesProviderId),
		staleTime: 30_000,
		throwOnError: false,
	});

	const facets = data?.facets;

	return useMemo(
		() => ({
			channelCounts: (facets?.channels ?? {}) as Partial<Record<ConversationChannel, number>>,
			statusCounts: (facets?.status ?? {}) as Partial<Record<ConversationStatus, number>>,
		}),
		[facets],
	);
}
